"use client"

import classNames from "classnames"
import dayjs from "dayjs"
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { message, Spin, Tooltip } from "antd"

import "./styles.scss"

import { useTranslations } from "next-intl"

import { SharePosterInnerProps, SharePosterRef } from "./types"

// CDN base URL for share poster assets
const ASSET_BASE_URL = "https://static.metal.com/common.metal.com/share"

// Asset URLs from CDN
const ASSETS = {
  close: `${ASSET_BASE_URL}/close.png`,
  download: `${ASSET_BASE_URL}/download.png`,
  downloadActive: `${ASSET_BASE_URL}/download-active.png`,
  footer: `${ASSET_BASE_URL}/footer_new.png`,
  header: `${ASSET_BASE_URL}/header-news.png`,
  headerAnnouncement: `${ASSET_BASE_URL}/header-announcement.png`,
  line: `${ASSET_BASE_URL}/line.png`,
  link: `${ASSET_BASE_URL}/link.png`,
  linkActive: `${ASSET_BASE_URL}/link-active.png`,
  qrcode: `${ASSET_BASE_URL}/qrcode.png`,
  shadowBg: `${ASSET_BASE_URL}/shadow-bg.png`,
}

// 海报最大高度，超过则显示"下载 App"遮罩
const POSTER_MAX_HEIGHT = 1330

const isHtmlLike = (text: string) =>
  /<(p|div|span|br|img|a|ul|ol|li|h[1-6]|table|tr|td|th|thead|tbody|strong|em|b|i|u|blockquote|pre|code|hr|section|article|header|footer|nav|main|aside|figure|figcaption)\b[^>]*>/i.test(
    text.trim()
  )

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")

const convertPlainTextToParagraphHtml = (text: string) => {
  const paragraphs = text
    .split(/\r\n|\n|\r/)
    .map((line) => line.trim())
    .filter(Boolean)

  if (paragraphs.length === 0) {
    return escapeHtml(text.trim())
  }

  return paragraphs.map((line) => `<div class="share_poster_paragraph">${escapeHtml(line)}</div>`).join("")
}

const formatPosterProfileHtml = (profile: string) => {
  const base = profile || ""
  if (!base) return ""

  if (isHtmlLike(base)) {
    return base.replace(/>\s*[\r\n]+\s*</g, "><").replace(/(<br\s*\/?>)+$/gi, "")
  }

  if (/(?:\r\n|\n|\r)/.test(base)) {
    return convertPlainTextToParagraphHtml(base)
  }

  return escapeHtml(base)
}

const getPosterDateTimeParts = (pubDate: number) => {
  const posterDate = dayjs.unix(pubDate)

  if (!posterDate.isValid()) {
    return {
      dateText: "",
      hourText: "--",
      minuteText: "--",
    }
  }

  return {
    dateText: posterDate.format("MMM DD, YYYY"),
    hourText: posterDate.format("HH"),
    minuteText: posterDate.format("mm"),
  }
}

const SharePosterInner = forwardRef<SharePosterRef, SharePosterInnerProps>(
  ({ open, data, config, onClose, silent, autoDownload, onDownloadComplete }, ref) => {
    const [messageApi, contextHolder] = message.useMessage()
    const [loading, setLoading] = useState<boolean>(false)
    const [canvasReady, setCanvasReady] = useState<boolean>(false)
    const [isHover, setIsHover] = useState({
      download: false,
      link: false,
    })
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const t = useTranslations("posterInfo")

    const [showDownloadOverlay, setShowDownloadOverlay] = useState(false)

    // Default texts
    const texts = {
      downloadTooltip: config.texts?.downloadTooltip || t("downloadTooltip"),
      copyTooltip: config.texts?.copyTooltip || t("copyTooltip"),
      copySuccess: config.texts?.copySuccess || t("copySuccess"),
      statement: config.texts?.statement || t("dataSourceStatement"),
      qrcodeText: config.texts?.qrcodeText || t("qrcodeText"),
      downloadAppText: config.texts?.downloadAppText || t("downloadAppText"),
    }

    const isAnnouncement = data.type === "announcement"
    const headerImage = isAnnouncement ? ASSETS.headerAnnouncement : ASSETS.header
    const posterDateTimeParts = getPosterDateTimeParts(data.pubDate)
    const profileHtml = (() => {
      const base = formatPosterProfileHtml(data.profile || "")
      if (!data.picUrls || data.picUrls === "undefined") return base
      const picsHtml = data.picUrls
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => `<img src="${item}" alt="" />`)
        .join("")
      if (!picsHtml) return base
      return `${base}<div class="share_poster_content_pics">${picsHtml}</div>`
    })()

    /**
     * Copy share link to clipboard
     */
    const copyShareLink = () => {
      const url = config.generateShareUrl(data.newsId)
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url)
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea")
        textArea.value = url
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand("copy")
        } catch (err) {
          console.error("Copy failed:", err)
        }
        document.body.removeChild(textArea)
      }
      messageApi.success(texts.copySuccess)
    }

    /**
     * Download the generated poster as PNG
     */
    const downloadPoster = () => {
      const canvas = canvasRef.current || (document.querySelector(`.flash-${data.newsId}`) as HTMLCanvasElement)
      if (canvas) {
        try {
          canvas.toBlob((blob) => {
            if (!blob) {
              messageApi.error("Failed to generate poster")
              return
            }
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `${data.titleEng || "Share-Poster"}.png`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
          }, "image/png")
        } catch (e) {
          console.error("Download failed:", e)
          messageApi.error("Failed to download poster")
        }
      }
    }

    useImperativeHandle(ref, () => ({
      download: downloadPoster,
    }))

    /**
     * Generate canvas from HTML element
     */
    const getCanvasData = async (element: HTMLDivElement) => {
      const canvas = canvasRef.current || (document.querySelector(`.flash-${data.newsId}`) as HTMLCanvasElement)
      // If canvasRef is not bound for some reason, try to update it manually, though ref binding is preferred
      if (canvasRef.current !== canvas && canvas) {
        // @ts-ignore - assigning to readonly property if we treat it strictly, but here we just ensure sync
        canvasRef.current = canvas
      }

      try {
        const html2canvas = (await import("html2canvas")).default
        const canvasData = await html2canvas(element, {
          scale: 3, // High quality output
          logging: false,
          useCORS: true, // Allow cross-origin images
          width: element.offsetWidth,
          height: element.offsetHeight,
        })

        // Draw to visible canvas
        if (canvas) {
          canvas.width = canvasData.width
          canvas.height = canvasData.height
          const ctx = canvas.getContext("2d")
          if (ctx) {
            ctx.drawImage(canvasData, 0, 0)
          }
        }

        setCanvasReady(true)
        setLoading(false)
      } catch (error) {
        console.error("Canvas generation failed:", error)
        messageApi.error("Failed to generate poster")
        setLoading(false)
      }
    }

    /**
     * Load and process the HTML content for canvas generation
     */
    const loadCanvasHtml = () => {
      const html: HTMLDivElement | null = document.querySelector(`.content-${data.newsId}`)

      if (html) {
        const contentHeight = html.offsetHeight
        const needOverlay = contentHeight > POSTER_MAX_HEIGHT
        setShowDownloadOverlay(needOverlay)

        // 等待遮罩状态更新后再生成 canvas
        setTimeout(() => {
          const updatedHtml: HTMLDivElement | null = document.querySelector(`.content-${data.newsId}`)
          if (updatedHtml) {
            getCanvasData(updatedHtml)
          }
        }, 50)
      }
    }

    useEffect(() => {
      if (open) {
        setLoading(true)
        setCanvasReady(false)
        // Wait for DOM to render before capturing
        setTimeout(() => {
          loadCanvasHtml()
        }, 100)
      }
    }, [data.newsId, open])

    // Handle auto download logic
    useEffect(() => {
      const loadingKey = "poster-loading"

      if (autoDownload) {
        if (!canvasReady) {
          messageApi.loading({ content: "Generating poster...", key: loadingKey, duration: 0 })
        } else {
          // Canvas is ready, trigger download
          // Add a small delay to ensure canvas rendering is fully committed
          setTimeout(() => {
            downloadPoster()
            onDownloadComplete?.()
          }, 100)
        }
      }

      return () => {
        if (autoDownload && !canvasReady) {
          messageApi.destroy(loadingKey)
        }
      }
    }, [autoDownload, canvasReady, messageApi])

    return (
      <div
        className={classNames("share_poster", { open, silent })}
        onClick={() => {
          if (!silent) {
            onClose()
            if (canvasRef.current) {
              canvasRef.current.height = 0
            }
          }
        }}
      >
        {contextHolder}
        {!silent && <Spin size="large" spinning={loading} />}
        <div className="share_poster_canvas_wrapper" onClick={(e) => e.stopPropagation()}>
          {!silent && <img onClick={onClose} className="close" src={ASSETS.close} alt="Close" />}
          <div className="share_poster_canvas">
            <canvas
              ref={canvasRef}
              className={`flash-${data.newsId}`}
              width="750"
              height="0"
              style={{ height: "auto", width: "375px" }}
            />
          </div>
          {open && !loading && !silent && (
            <div className="action">
              <Tooltip
                title={texts.downloadTooltip}
                classNames={{
                  root: "poster_tip_wrap",
                }}
                getPopupContainer={(trigger) => trigger}
              >
                <span
                  onMouseEnter={() => setIsHover({ ...isHover, download: true })}
                  onMouseLeave={() => setIsHover({ ...isHover, download: false })}
                >
                  <img
                    onClick={downloadPoster}
                    src={isHover.download ? ASSETS.downloadActive : ASSETS.download}
                    alt="Download"
                  />
                </span>
              </Tooltip>
              <Tooltip
                title={texts.copyTooltip}
                classNames={{
                  root: "poster_tip_wrap",
                }}
                getPopupContainer={(trigger) => trigger}
              >
                <span
                  onMouseEnter={() => setIsHover({ ...isHover, link: true })}
                  onMouseLeave={() => setIsHover({ ...isHover, link: false })}
                >
                  <img onClick={copyShareLink} src={isHover.link ? ASSETS.linkActive : ASSETS.link} alt="Copy Link" />
                </span>
              </Tooltip>
            </div>
          )}
        </div>

        {/* Hidden content for canvas generation */}
        {loading && (
          <div className={`share_poster_content content-${data.newsId}`}>
            <div className="share_poster_content_header">
              <img src={headerImage} alt="Header" />
            </div>
            {/* 阴影包装层 - 4 层渐变模拟柔和阴影（html2canvas 兼容） */}
            <div className="share_poster_shadow_wrapper">
              <div className="share_poster_shadow_1" />
              <div className="share_poster_shadow_2" />
              <div className="share_poster_shadow_3" />
              <div className="share_poster_shadow_4" />
              <div
                className="share_poster_content_body"
                // style={{
                //   backgroundImage: `url(${ASSETS.shadowBg})`,
                //   backgroundSize: "100% 100%",
                //   backgroundRepeat: "no-repeat",
                //   backgroundPosition: "center",
                // }}
              >
                {/* <div style={{ height: emptyHeight + "px" }}></div> */}
                <div className="share_poster_content_time">
                  <span className="share_poster_content_time_value">
                    <span>{posterDateTimeParts.dateText}</span>
                    <span
                      className="share_poster_content_clock"
                      aria-label={`${posterDateTimeParts.hourText}:${posterDateTimeParts.minuteText}`}
                    >
                      <span className="share_poster_content_clock_hour">{posterDateTimeParts.hourText}</span>
                      <span className="share_poster_content_clock_separator">:</span>
                      <span className="share_poster_content_clock_minute">{posterDateTimeParts.minuteText}</span>
                    </span>
                  </span>
                  <img src={ASSETS.line} alt="Line" />
                </div>
                {/* Show title for both news and announcement types */}
                {data.titleEng && <div className="share_poster_content_title">{data.titleEng}</div>}
                <div
                  className={classNames("share_poster_content_profile", { with_overlay: showDownloadOverlay })}
                  dangerouslySetInnerHTML={{
                    __html: profileHtml,
                  }}
                />
                {/* 内容过长时显示遮罩提示 */}
                {showDownloadOverlay && (
                  <div className="share_poster_download_overlay">
                    <div className="share_poster_download_overlay_text">{texts.downloadAppText}</div>
                  </div>
                )}
                {!isAnnouncement && <div className="share_poster_statement">{texts.statement}</div>}
                <div className="share_poster_content_card">
                  <div
                    className="share_poster_content_card_item"
                    dangerouslySetInnerHTML={{ __html: texts.qrcodeText }}
                  />
                  <img src={ASSETS.qrcode} alt="QR Code" />
                </div>
              </div>
              {/* <div style={{ height: emptyHeight + "px" }}></div> */}
            </div>
            <div className="share_poster_content_footer">
              <img src={ASSETS.footer} alt="Footer" />
            </div>
          </div>
        )}
      </div>
    )
  }
)

SharePosterInner.displayName = "SharePosterInner"

export default SharePosterInner
